"use client"

import { useCallback, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { useAuthStore } from "@/lib/store/auth-store"
import { BeUser } from "@/lib/types/user"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { useAuthDialog } from "./context"
import { OtpStep, PhoneStep, PinStep } from "./login-steps"
import { AuthStep } from "./types"

export function LoginDialog() {
  const open = useAuthDialog((state) => state.open)
  const onOpenChange = useAuthDialog((state) => state.onOpenChange)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentStep, setCurrentStep] = useState<AuthStep>("PHONE")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [resendCooldown, setResendCooldown] = useState(0)
  const queryClient = useQueryClient()

  // Start cooldown timer for OTP resend
  const startResendCooldown = useCallback(() => {
    setResendCooldown(60) // 60 seconds cooldown
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  // Reset state when dialog closes
  const handleDialogChange = useCallback(
    (open: boolean) => {
      if (!open) {
        // Reset state
        setCurrentStep("PHONE")
        setPhoneNumber("")
        setResendCooldown(0)
      }
      onOpenChange(open)
    },
    [onOpenChange]
  )

  // Handle successful login
  const handleLoginSuccess = useCallback(
    (data: BeUser) => {
      // Set tokens in auth store
      const accessTokenExpiry = new Date(data.expire_at).getTime() / 1000
      const refreshTokenExpiry = new Date(data.rt_expire_at).getTime() / 1000

      useAuthStore
        .getState()
        .setTokens(
          data.access_token,
          data.refresh_token,
          accessTokenExpiry,
          refreshTokenExpiry
        )

      // Set user data in auth store
      useAuthStore.getState().setUser(data)

      queryClient.invalidateQueries({
        queryKey: ["cart"],
      })

      toast.success("Đăng nhập thành công!")
      onOpenChange(false)
    },
    [onOpenChange, queryClient]
  )

  // Handle step transitions and login success
  const handleStepSuccess = useCallback(
    (nextStep: AuthStep, data?: unknown) => {
      if (nextStep === "SUCCESS" && data) {
        handleLoginSuccess(data as BeUser)
      } else {
        setCurrentStep(nextStep)
      }
      setIsSubmitting(false)
    },
    [handleLoginSuccess]
  )

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent
        data-testid="login-dialog"
        className="md:min-w-[680px] p-1 rounded-2xl"
      >
        <DialogHeader className="p-6 pb-2 text-left">
          <DialogTitle className="text-2xl font-bold text-gray-900 max-md:text-xl">
            {currentStep === "PHONE" && "Khám phá ẩm thực cùng Be!"}
            {currentStep === "PIN" && "Nhập mật khẩu"}
            {currentStep === "OTP" && "Nhập mã OTP"}
          </DialogTitle>
          <DialogDescription className="text-base text-gray-700 max-md:text-base">
            {currentStep === "PHONE" &&
              "Đăng nhập/Đăng ký tài khoản be ngay bây giờ"}
            {currentStep === "PIN" &&
              "Bạn đang đăng nhập với số điện thoại " + phoneNumber}
            {currentStep === "OTP" &&
              `Mã OTP đã được gửi tới số: ${phoneNumber}`}
          </DialogDescription>
        </DialogHeader>

        {/* Render the appropriate step component based on currentStep */}
        {currentStep === "PHONE" && (
          <PhoneStep
            phoneNumber={phoneNumber}
            onPhoneChange={setPhoneNumber}
            onSuccess={handleStepSuccess}
            startResendCooldown={startResendCooldown}
            isSubmitting={isSubmitting}
          />
        )}

        {currentStep === "PIN" && (
          <PinStep
            phoneNumber={phoneNumber}
            onSuccess={handleStepSuccess}
            startResendCooldown={startResendCooldown}
            isSubmitting={isSubmitting}
          />
        )}

        {currentStep === "OTP" && (
          <OtpStep
            phoneNumber={phoneNumber}
            onSuccess={handleStepSuccess}
            resendCooldown={resendCooldown}
            startResendCooldown={startResendCooldown}
            isSubmitting={isSubmitting}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
